import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { ListingCard } from "@/components/ListingCard";
import { Input } from "@/components/ui/input";
import { MagnifyingGlass, MapPin, Globe } from "@phosphor-icons/react";

const CATEGORIES = ["All", "Mountains", "Beaches", "Culture", "Nature", "Adventure"];

export default function Explore() {
  const [searchParams] = useSearchParams();
  const [listings, setListings] = useState([]);
  const [allListings, setAllListings] = useState([]);
  const [category, setCategory] = useState(searchParams.get("category") || "All");
  const [q, setQ] = useState(searchParams.get("q") || "");
  const [maxPrice, setMaxPrice] = useState(searchParams.get("max") || "");
  const [loading, setLoading] = useState(true);
  const [showSuggest, setShowSuggest] = useState(false);
  const searchRef = useRef(null);

  // React to URL param changes (e.g. arriving from voice/typed hero search).
  useEffect(() => {
    setCategory(searchParams.get("category") || "All");
    setQ(searchParams.get("q") || "");
    setMaxPrice(searchParams.get("max") || "");
  }, [searchParams]);

  // Load full catalogue once for building search suggestions.
  useEffect(() => {
    api.get("/listings").then((r) => setAllListings(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (category !== "All") params.category = category;
    if (q) params.q = q;
    if (maxPrice) params.max_price = maxPrice;
    const t = setTimeout(() => {
      api.get("/listings", { params }).then((r) => { setListings(r.data); setLoading(false); }).catch(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [category, q, maxPrice]);

  // Close suggestions on outside click.
  useEffect(() => {
    const onClick = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) setShowSuggest(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const countries = useMemo(
    () => Array.from(new Set(allListings.map((l) => l.country).filter(Boolean))).sort(),
    [allListings]
  );

  const suggestions = useMemo(() => {
    const query = q.trim().toLowerCase();
    const countryItems = countries
      .filter((c) => !query || c.toLowerCase().includes(query))
      .map((c) => ({ type: "country", label: c, value: c }));
    const placeItems = allListings
      .filter((l) => !query || l.title.toLowerCase().includes(query) || (l.location || "").toLowerCase().includes(query))
      .map((l) => ({ type: "place", label: l.title, sub: l.location, value: l.title }));
    return [...countryItems, ...placeItems].slice(0, 10);
  }, [q, countries, allListings]);

  const pick = (value) => {
    setQ(value);
    setShowSuggest(false);
  };

  return (
    <div className="pt-24 min-h-screen">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-8">
        <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#4A6E7C]">Destinations</span>
        <h1 className="font-display text-4xl sm:text-5xl font-bold mt-2 mb-8">Where to next?</h1>

        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <div className="relative flex-1 max-w-md" ref={searchRef}>
            <MagnifyingGlass size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#63675E] z-10" />
            <Input
              value={q}
              onChange={(e) => { setQ(e.target.value); setShowSuggest(true); }}
              onFocus={() => setShowSuggest(true)}
              placeholder="Search destinations or countries…"
              className="pl-11 h-12 rounded-full bg-white border-[#D2D5CD]"
              data-testid="explore-search-input"
            />
            {showSuggest && suggestions.length > 0 && (
              <div className="absolute z-20 mt-2 w-full max-h-80 overflow-y-auto rounded-2xl border border-[#D2D5CD] bg-white shadow-lg py-2" data-testid="search-suggestions">
                {q === "" && (
                  <div className="px-4 py-1.5 text-[11px] uppercase tracking-wider font-bold text-[#8B8E85]">Countries & places</div>
                )}
                {suggestions.map((s) => (
                  <button
                    key={`${s.type}-${s.value}`}
                    onClick={() => pick(s.value)}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-[#EAEBE6] transition-colors duration-150"
                    data-testid={`suggestion-${s.type}-${s.value.toLowerCase().replace(/\s/g, "-")}`}
                  >
                    {s.type === "country" ? (
                      <Globe size={18} weight="duotone" className="text-[#4A6E7C] shrink-0" />
                    ) : (
                      <MapPin size={18} weight="duotone" className="text-[#E8B291] shrink-0" />
                    )}
                    <span className="flex-1 min-w-0">
                      <span className="font-medium text-[#1A1C19] truncate block">{s.label}</span>
                      {s.sub && <span className="text-xs text-[#63675E]">{s.sub}</span>}
                    </span>
                    <span className="text-[10px] uppercase tracking-wide text-[#8B8E85]">{s.type}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="flex gap-2 flex-wrap">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                onClick={() => setCategory(c)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200 ${
                  category === c ? "bg-[#2E523A] text-white" : "bg-white border border-[#D2D5CD] hover:border-[#2E523A]/40"
                }`}
                data-testid={`category-${c.toLowerCase()}`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>

        {(q || maxPrice) && (
          <div className="mb-6 flex items-center flex-wrap gap-2 text-sm text-[#63675E]" data-testid="active-search">
            Showing results
            {q && <>for <span className="font-semibold text-[#1A1C19]">“{q}”</span></>}
            {maxPrice && <span className="px-2.5 py-1 rounded-full bg-[#EAEBE6] font-semibold text-[#1A1C19]" data-testid="active-maxprice">under ${maxPrice}</span>}
            <button onClick={() => { setQ(""); setMaxPrice(""); }} className="text-[#2E523A] font-semibold hover:underline" data-testid="clear-search">Clear</button>
          </div>
        )}

        {loading ? (
          <p className="text-muted-foreground py-16 text-center">Loading destinations…</p>
        ) : listings.length === 0 ? (
          <p className="text-muted-foreground py-16 text-center" data-testid="no-listings">No destinations match your search.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="listings-grid">
            {listings.map((l, i) => (
              <ListingCard key={l.id} listing={l} index={i} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
